import { type JobProgressionStepStatus } from '@/features/jobs/taxes/progression/JobProgression'
import { type SearchableJob } from '@/features/jobs/list/SearchableJob'
import { type JobStatus } from '@/features/jobs/job-status'
import type { WaitingOn } from '@/features/jobs/Job'

/**
 * Which side of the job the viewer is on, which decides the default saved
 * filters and how some columns read. Admins watch both sides, so no job is
 * ever waiting on them.
 */
export type JobListPerspective = 'Pro' | 'Client' | 'Admin'

export const jobListPerspectives: readonly JobListPerspective[] = [
  'Pro',
  'Client',
  'Admin',
]

/**
 * Progression steps where the pro holds the next action.
 *
 * `ExtensionNotStarted` belongs here, but the search filter parser in
 * dotnet-api rejects it, so those jobs fall back to `waiting_on` instead.
 */
export const proOwnedSteps: JobProgressionStepStatus[] = [
  'NotStarted',
  'JobStarted',
  'DraftRejected',
  'DraftApproved',
  'AuthorizationSigned',
  'ReadyToFile',
  'ExtensionRejected',
]

/**
 * Progression steps where the client holds the next action.
 */
export const clientOwnedSteps: JobProgressionStepStatus[] = [
  'OpenItems',
  'DraftInReview',
  'AuthorizationRequested',
  'ProgressionWaitingOnClient',
]

/**
 * Statuses where the work is underway, so the turn can belong to either side.
 */
export const inProgressStatuses: JobStatus[] = ['Claimed', 'Idle']

/**
 * Who holds the next action on a job. The search's Waiting on filter
 * (`withWaitingOn` in the query builder) applies the same rules, so a row
 * never contradicts its tab; change both together.
 */
export function whoseTurn(job: SearchableJob): WaitingOn | null {
  if (job.status === 'InfoGathering') {
    return 'Client'
  }
  if (!inProgressStatuses.includes(job.status)) {
    return null
  }
  if (proOwnedSteps.includes(job.progressionStep)) {
    return 'Provider'
  }
  if (clientOwnedSteps.includes(job.progressionStep)) {
    return 'Client'
  }

  return job.waitingOn
}
